/**
 * This file contains keys and paths to fields that
 * are used in Next. These can be used to "inject"
 * certain behavior like validations.
 */

/**
 * Keys of known fieldsets for Next.
 */
/* eslint-disable @typescript-eslint/naming-convention */
export enum DeclarativeFormFieldsetKey {
  Personal = 'personalDataKeys',
  ContactInfo = 'contactDataKeys',
  Contribution = 'contributionData',
  Sponsorships = 'sponsorships',
  // Fieldsets for bank data
  BankDataSepa = 'sepaBankData',
  BankDataSepaEur = 'sepaEurBankData',
  BankDataSepaExternalEpms = 'sepaExternalEpmsBankData',
  BankDataSepaRaisenowEpms = 'sepaRaiseNowEpmsBankData',
  // Swiss bank data
  BankDataDirectDebitCH = 'swissDirectDebit',
  BankDataDirectDebitCHRaisenowEpms = 'swissDirectDebitRaiseNowEpms',
  BankDataDirectDebitCHExternalEpms = 'swissDirectDebitExternalEpms',
  BankDataPostFinance = 'postfinanceBankData',
  BankDataPostFinanceRaisenowEpms = 'postfinanceRaiseNowEpmsBankData',
  BankDataPostFinanceExternalEpms = 'postfinanceExternalEpmsBankData',
  // Uk bank data
  BankDataSortCodeUK = 'sortCodesBankData',
  Signature = 'signature',
}
/* eslint-enable @typescript-eslint/naming-convention */

/**
 * Keys of known fields for Next.
 */
export enum DeclarativeFormFieldKey {
  // Bank data fields
  BankIbanOrNumber = 'account_iban_or_number',
  BankBicOrBankCode = 'account_bic_or_bankcode',
  BankName = 'account_bank_name',
  // Contact Data
  ContactDataOptIn = 'contact_data_conf',
  Email = 'email',
  PhoneNumber = 'phone_number',
  MobileNumber = 'mobile_number',
  // Contribution data
  Currency = 'currency',
  DonationAmount = 'donation_amount',
  DonationAmountPerInterval = 'donation_amount_per_interval',
  Interval = 'direct_debit_interval',
  PaymentMethod = 'payment_method',
  // Personal data
  CorrespondenceLanguage = 'correspondence_language',
  // Sponsorships
  Sponsorship = 'sponsorship',
  // Signature
  UseSelfCheckout = 'self_checkout', // sign on tablet or supporter device
}

/**
 * Describes the path to a certain field
 */
type DeclarativeFormFieldPath<
  FS extends DeclarativeFormFieldsetKey,
  F extends DeclarativeFormFieldKey,
> = [`${FS}`, `${F}`];

/**
 * @param fieldset Key of the fieldset
 * @param field Key of the field
 * @returns
 * A typed version of the path to the field
 */
function buildPath<
  FS extends DeclarativeFormFieldsetKey,
  F extends DeclarativeFormFieldKey,
>(fieldset: FS, field: F): DeclarativeFormFieldPath<FS, F> {
  return [`${fieldset}`, `${field}`];
}

/**
 * Paths of known fields in Next.
 */
export const declarativeFormFieldPath = Object.freeze({
  correspondenceLanguage: buildPath(
    DeclarativeFormFieldsetKey.Personal,
    DeclarativeFormFieldKey.CorrespondenceLanguage,
  ),
  contactDataOptIn: buildPath(
    DeclarativeFormFieldsetKey.ContactInfo,
    DeclarativeFormFieldKey.ContactDataOptIn,
  ),
  currency: buildPath(
    DeclarativeFormFieldsetKey.Contribution,
    DeclarativeFormFieldKey.Currency,
  ),
  donationAmount: buildPath(
    DeclarativeFormFieldsetKey.Contribution,
    DeclarativeFormFieldKey.DonationAmount,
  ),
  donationAmountPerInterval: buildPath(
    DeclarativeFormFieldsetKey.Contribution,
    DeclarativeFormFieldKey.DonationAmountPerInterval,
  ),
  directDebitInterval: buildPath(
    DeclarativeFormFieldsetKey.Contribution,
    DeclarativeFormFieldKey.Interval,
  ),
  paymentMethod: buildPath(
    DeclarativeFormFieldsetKey.Contribution,
    DeclarativeFormFieldKey.PaymentMethod,
  ),
  sponsorship: buildPath(
    DeclarativeFormFieldsetKey.Sponsorships,
    DeclarativeFormFieldKey.Sponsorship,
  ),
  useSelfCheckout: buildPath(
    DeclarativeFormFieldsetKey.Signature,
    DeclarativeFormFieldKey.UseSelfCheckout,
  ),
});

/**
 * Keys of fieldsets that contain an IBAN input field
 */
export const declarativeFormFieldsetsWithIban: readonly string[] = [
  DeclarativeFormFieldsetKey.BankDataSepa,
  DeclarativeFormFieldsetKey.BankDataSepaEur,
  DeclarativeFormFieldsetKey.BankDataDirectDebitCH,
  DeclarativeFormFieldsetKey.BankDataPostFinance,
  DeclarativeFormFieldsetKey.BankDataSepaExternalEpms,
  DeclarativeFormFieldsetKey.BankDataSepaRaisenowEpms,
  DeclarativeFormFieldsetKey.BankDataDirectDebitCHExternalEpms,
  DeclarativeFormFieldsetKey.BankDataDirectDebitCHRaisenowEpms,
  DeclarativeFormFieldsetKey.BankDataPostFinanceExternalEpms,
  DeclarativeFormFieldsetKey.BankDataPostFinanceRaisenowEpms,
];
